import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/landing/Logo";
import { adminListCats, adminCreateCat, adminUpdateCat, adminDeleteCat } from "@/lib/blog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2 } from "lucide-react";

export default function AdminBlogCategories() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ name: "", description: "", icon: "Tag", color: "emerald" });

  const load = async () => {
    setLoading(true);
    try { setItems(await adminListCats()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!draft.name) return;
    try { await adminCreateCat(draft); setDraft({ name: "", description: "", icon: "Tag", color: "emerald" }); toast.success("Category created"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not create"); }
  };

  const onPatch = (id, patch) => setItems((prev) => prev.map((c) => c.id === id ? { ...c, ...patch, _dirty: true } : c));
  const saveOne = async (c) => { try { await adminUpdateCat(c.id, { name: c.name, slug: c.slug, description: c.description, icon: c.icon, color: c.color, order: c.order }); toast.success("Saved"); load(); } catch (err) { toast.error("Save failed"); } };
  const delOne = async (c) => { if (!window.confirm(`Delete category "${c.name}"? Posts in this category will be uncategorised.`)) return; await adminDeleteCat(c.id); toast.success("Deleted"); load(); };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-blog-categories">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => nav("/admin/blog")} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1.5" />Blog</Button>
          <Logo />
          <span className="inline-flex items-center text-[11px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-slate-900 text-white">Categories</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-8 space-y-6">
        <section className="rounded-2xl bg-white border border-slate-200 p-5">
          <h2 className="font-bold text-slate-900 mb-3">Add category</h2>
          <form onSubmit={create} className="flex flex-col sm:grid sm:grid-cols-[1fr_120px_120px_auto] gap-2 items-end" data-testid="cat-create-form">
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name" required data-testid="cat-name" />
            <Input value={draft.icon} onChange={(e) => setDraft({ ...draft, icon: e.target.value })} placeholder="Icon (lucide)" data-testid="cat-icon" />
            <Input value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} placeholder="Color" />
            <Button type="submit" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="cat-create-submit"><Plus className="h-4 w-4 mr-1" />Add</Button>
            <Textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Description (optional)" rows={2} className="sm:col-span-4" />
          </form>
        </section>

        <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-500" /></div>
            : items.length === 0 ? <div className="py-10 text-center text-slate-500 text-sm">No categories yet.</div>
            : (
              <ul className="divide-y divide-slate-100" data-testid="cat-list">
                {items.map((c) => (
                  <li key={c.id} className="p-4 flex flex-col sm:grid sm:grid-cols-[1fr_120px_140px_60px_auto] gap-2 items-start sm:items-center" data-testid={`cat-row-${c.slug}`}>
                    <Input value={c.name} onChange={(e) => onPatch(c.id, { name: e.target.value })} />
                    <Input value={c.icon || ""} onChange={(e) => onPatch(c.id, { icon: e.target.value })} placeholder="Icon" />
                    <Input value={c.slug} onChange={(e) => onPatch(c.id, { slug: e.target.value })} />
                    <span className="text-xs text-slate-500 text-center">{c.post_count} posts</span>
                    <div className="flex gap-1.5 justify-end">
                      {c._dirty && <Button size="sm" onClick={() => saveOne(c)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`cat-save-${c.slug}`}>Save</Button>}
                      <Button size="sm" variant="ghost" onClick={() => delOne(c)} className="text-rose-600" data-testid={`cat-delete-${c.slug}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </section>
      </main>
    </div>
  );
}
